import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Brain, Heart, Users, Briefcase, UserCheck } from 'lucide-react';

const TreatmentsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Handle anchor scrolling
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  const treatments = [
    {
      id: 'cbt',
      title: 'Cognitive Behavioral Therapy (CBT)',
      icon: Brain,
      color: 'blue',
      description: 'Evidence-based treatment for depression, anxiety, OCD, PTSD, and more.',
      benefits: [
        'Proven effectiveness for various mental health conditions',
        'Short-term, goal-oriented approach',
        'Learn practical coping skills',
        'Address negative thought patterns'
      ]
    },
    {
      id: 'eap',
      title: 'Employee Assistance Program (EAP)',
      icon: Briefcase,
      color: 'green',
      description: 'Workplace mental health and wellbeing support for organizations.',
      benefits: [
        'Confidential counseling services',
        'Work-life balance support',
        'Stress management programs',
        'Crisis intervention'
      ]
    },
    {
      id: 'counseling',
      title: 'Counseling',
      icon: Heart,
      color: 'red',
      description: 'Professional support for life challenges, emotional wellbeing, and personal growth.',
      benefits: [
        'Safe, non-judgmental environment',
        'Person-centered approach',
        'Support for various life transitions',
        'Build emotional resilience'
      ]
    },
    {
      id: 'couples-therapy',
      title: 'Couples Therapy',
      icon: Users,
      color: 'purple',
      description: 'Relationship counseling to improve communication and strengthen bonds.',
      benefits: [
        'Improve communication skills',
        'Resolve conflicts effectively',
        'Rebuild trust and intimacy',
        'Strengthen relationship bonds'
      ]
    },
    {
      id: 'clinical-psychology',
      title: 'Clinical Psychology',
      icon: UserCheck,
      color: 'teal',
      description: 'Comprehensive psychological assessment, diagnosis, and treatment.',
      benefits: [
        'Thorough psychological assessment',
        'Evidence-based interventions',
        'Treatment for complex conditions',
        'Holistic care approach'
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        button: 'bg-green-600 hover:bg-green-700'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: 'text-red-600',
        button: 'bg-red-600 hover:bg-red-700'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        icon: 'text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        icon: 'text-teal-600',
        button: 'bg-teal-600 hover:bg-teal-700'
      }
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-700 to-slate-900 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our Treatment Approaches
          </h1>
          <p className="text-xl text-slate-200 mb-8 max-w-3xl mx-auto">
            Evidence-based therapies tailored to your unique needs. Choose the approach that's right for you.
          </p>
          <button
            onClick={() => navigate('/book-now')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center gap-2"
          >
            <Calendar className="w-5 h-5" />
            Book an Appointment
          </button>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {treatments.map((treatment) => (
              <a
                key={treatment.id}
                href={`#${treatment.id}`}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
              >
                {treatment.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {treatments.map((treatment, index) => {
          const Icon = treatment.icon;
          const colors = getColorClasses(treatment.color);
          
          return (
            <section
              key={treatment.id}
              id={treatment.id}
              className={`mb-16 scroll-mt-24 ${colors.bg} border-2 ${colors.border} rounded-xl p-8`}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className={`${colors.bg} p-4 rounded-full`}>
                  <Icon className={`w-10 h-10 ${colors.icon}`} />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">{treatment.title}</h2>
              </div>
              
              <p className="text-lg text-gray-700 mb-6">{treatment.description}</p>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Benefits:</h3>
                <ul className="grid md:grid-cols-2 gap-3">
                  {treatment.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className={`w-2 h-2 ${colors.icon.replace('text', 'bg')} rounded-full mt-2 flex-shrink-0`}></span>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/book-now')}
                  className={`${colors.button} text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200`}
                >
                  Book {treatment.title}
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
                >
                  Learn More
                </button>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-green-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Begin Your Journey?</h2>
          <p className="text-lg mb-8 opacity-90">
            Our expert team is here to support you. Book an appointment or contact us to learn more.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/book-now')}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Book Now
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TreatmentsPage;
